import React, { useState, useRef, forwardRef, useMemo, useCallback, useEffect } from 'react';
import { I18n } from 'src/translations/i18n';
import InputMask from 'react-input-mask';
import debounceFn from 'debounce';
import { Grid, Button, Popper, Grow, Paper, makeStyles } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { FORBIDDEN_ACTIVE_DIRECTORY_CHARS_REGEX, FORBIDDEN_EMAIL_CHARS_REGEX, FORBIDDEN_TEXT_CHARS_REGEX } from 'src/constants/regExps';
import Utils from 'src/utils';
import { getCharactersFromRegex } from 'src/helpers/FPUtils';
import FormControlLabel from '../../Form/FormControlLabel';
const useStyles = makeStyles(({ palette }) => ({
    input: {
        color: palette.input.color,
        backgroundColor: palette.input.background,
        border: `1px solid ${palette.ddSelector.borderColor}`,
        borderRadius: 2,
        '&:disabled': {
            backgroundColor: palette.disabled,
            color: palette.disabledText
        }
    },
    errorField: {
        '& label': {
            color: '#a94442'
        },
        '& input': {
            borderColor: '#a94442'
        }
    },
    errorText: {
        color: '#a94442'
    },
    toolTip: {
        zIndex: 1300
    },
    eyeButton: {
        height: 34,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderColor: '#ccc',
        boxShadow: 'none',
        borderLeft: 'none',
        marginRight: -1,
        '&:hover': {
            boxShadow: 'none'
        }
    },
    passwordInput: {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0
    },
    paper: {
        padding: '3px 8px',
        textAlign: 'center',
        backgroundColor: '#000',
        maxWidth: 200
    },
    content: {
        fontSize: '12px',
        color: '#FFF'
    },
    popperShowed: {
        zIndex: 1400
    },
    popperHidden: {
        zIndex: -1
    }
}));
let timeoutId = 0;
const TextInput = forwardRef(({ onChange = () => { }, onBlur = () => { }, label = '', mouseInKey = '', isRequired = false, className = '', type = 'text', bottomError = false, containerClassName = '', labelContainerClassName = '', errorText = '', isValid = true, value: propValue = '', mask = '', prefix = '', postfix = '', disableValidation = false, disabledChars = null, debounce = 0, maskPlaceHolder = '_', showPasswordRef, ...props }, ref) => {
    const classes = useStyles();
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(String(propValue));
    const [charactersWarningShow, setCharactersWarningShow] = useState(false);
    const [negativeNumberWarningShow, setNegativeNumberWarningShow] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    if (showPasswordRef) {
        showPasswordRef.current = {
            value: showPassword,
            setValue: setShowPassword
        };
    }
    const usingSyncedLocalInput = !!debounce;
    const value = usingSyncedLocalInput ? localValue : propValue;
    const disabledCharacters = useMemo(() => {
        if (disabledChars) {
            return disabledChars;
        }
        if (type === 'email') {
            return FORBIDDEN_EMAIL_CHARS_REGEX;
        }
        if (type === 'ad-name') {
            return FORBIDDEN_ACTIVE_DIRECTORY_CHARS_REGEX;
        }
        return FORBIDDEN_TEXT_CHARS_REGEX;
    }, [type, disabledChars]);
    const emitChange = useMemo(() => (debounceFn(onChange, debounce)), [onChange, debounce]);
    const updateInputValue = useCallback((val) => {
        if (usingSyncedLocalInput) {
            setLocalValue(val);
            emitChange(val);
        }
        else {
            onChange(val);
        }
    }, [emitChange, onChange, usingSyncedLocalInput]);
    useEffect(() => {
        const propVal = String(propValue);
        if (usingSyncedLocalInput && (propVal !== localValue)) {
            setLocalValue(propVal);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propValue]);
    const handleChange = useCallback(({ nativeEvent }) => {
        if (type === 'password' || disableValidation) {
            updateInputValue(nativeEvent.target.value);
        }
        else if (type === 'number-positive') {
            const inValid = nativeEvent.target.value.includes('-') || nativeEvent.data === '-' || nativeEvent.target.value === '0';
            if (inValid) {
                setNegativeNumberWarningShow(true);
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => setNegativeNumberWarningShow(false), 3000);
            }
            else {
                updateInputValue(nativeEvent.target.value);
            }
        }
        else {
            const { valid, value: validValue } = Utils.strings.validateByCharacters(nativeEvent.target.value, disabledCharacters);
            if (!valid) {
                setCharactersWarningShow(true);
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => setCharactersWarningShow(false), 3000);
            }
            updateInputValue(validValue);
        }
    }, [type, disableValidation, updateInputValue, disabledCharacters]);
    const inputType = useMemo(() => {
        switch (type) {
            case 'password': return showPassword ? 'text' : type;
            case 'number-positive': return 'number';
            default: return type || 'text';
        }
    }, [type, showPassword]);
    const inputProps = useMemo(() => ({
        ...props,
        ref: inputRef,
        value,
        className: `form-control ${className} ${classes.input}`,
        onChange: handleChange,
        onBlur
    }), [props, value, className, classes.input, handleChange, onBlur]);
    const renderInput = useCallback(
    // @ts-ignore
    () => (type === 'textarea' ? React.createElement("textarea", { ...inputProps }) : React.createElement("input", { ...inputProps, type: inputType })), [type, inputProps, inputType]);
    const toggleShowPassword = () => setShowPassword(!showPassword);
    const warningPopperShowed = useMemo(() => charactersWarningShow || negativeNumberWarningShow, [charactersWarningShow, negativeNumberWarningShow]);
    const warningPopperMessage = useMemo(() => {
        if (charactersWarningShow) {
            return `${I18n.t('ForbiddenSymbol')} ${getCharactersFromRegex(disabledCharacters)}`;
        }
        if (negativeNumberWarningShow) {
            return I18n.t('ForbiddenNegativeSymbol');
        }
        return '';
    }, [charactersWarningShow, negativeNumberWarningShow, disabledCharacters]);
    return (
    // @ts-ignore
    React.createElement(Grid, { item: true, xs: "auto", className: `form-input column-input ${containerClassName} ${!isValid ? classes.errorField : ''}`, ref: ref },
        (label || (!isValid && errorText)) && (React.createElement(Grid, { container: true, spacing: 2, justifyContent: "space-between", wrap: "nowrap", className: labelContainerClassName },
            label && (React.createElement(Grid, { item: true },
                React.createElement(FormControlLabel, { title: I18n.t(label), mouseInKey: mouseInKey, isRequired: isRequired, isValid: isValid }))),
            !isValid && errorText && !bottomError && (React.createElement(Grid, { item: true },
                React.createElement("span", { className: classes.errorText }, errorText))))),
        React.createElement(Grid, { container: true, wrap: "nowrap" },
            mask
                ? (React.createElement(InputMask, { ref: inputRef, mask: mask, onChange: (e) => updateInputValue(e.nativeEvent.target.value), value: value, className: `form-control ${className} ${classes.input}`, maskPlaceholder: maskPlaceHolder, alwaysShowMask: true }))
                : renderInput(),
            type === 'password' && (React.createElement(Button, { classes: { root: classes.eyeButton }, color: "secondary", variant: "contained", "aria-label": "edit", onClick: toggleShowPassword }, showPassword ? React.createElement(VisibilityIcon, null) : React.createElement(VisibilityOffIcon, null)))),
        React.createElement(Popper, { id: "mouse-over-popover", placement: "bottom-end", open: warningPopperShowed, anchorEl: inputRef.current, className: warningPopperShowed ? classes.popperShowed : classes.popperHidden },
            React.createElement(Grow, { in: warningPopperShowed, style: { transformOrigin: '0 0 0', width: 'auto' } },
                React.createElement(Paper, { className: classes.paper },
                    React.createElement("div", { className: classes.content }, warningPopperMessage)))),
        !isValid && errorText && bottomError && (React.createElement(Grid, { item: true },
            React.createElement("span", { className: classes.errorText }, errorText)))));
});
TextInput.displayName = 'TextInput';
export default TextInput;
